/* eslint-disable @typescript-eslint/no-use-before-define */
// Abstract base ↔ concrete subclass references below are intentional and safe
// under class hoisting — every reference site runs after both classes are fully
// initialized.
import { case as case_, field, union } from "@stellar/js-xdr";
import { XdrError, type XdrType } from "@stellar/js-xdr";
import { XdrValue } from "../values/xdr-value.js";
import { ScEnvMetaKind } from "./sc-env-meta-kind.js";
import {
  ScEnvMetaEntryInterfaceVersion,
  type ScEnvMetaEntryInterfaceVersionWire,
} from "./sc-env-meta-entry-interface-version.js";

export type ScEnvMetaEntryWire = {
  kind: 0;
  interfaceVersion: ScEnvMetaEntryInterfaceVersionWire;
};

export type ScEnvMetaEntryVariantName = "scEnvMetaKindInterfaceVersion";

/**
 * ```xdr
 * union SCEnvMetaEntry switch (SCEnvMetaKind kind)
 * {
 * case SC_ENV_META_KIND_INTERFACE_VERSION:
 *     struct {
 *         uint32 protocol;
 *         uint32 preRelease;
 *     } interfaceVersion;
 * };
 * ```
 */
abstract class ScEnvMetaEntryBase extends XdrValue {
  abstract readonly type: ScEnvMetaEntryVariantName;

  constructor() {
    super();
    // `new.target`, not an unconditional throw: every arm subclass reaches
    // this constructor through `super()`, void arms via an implicit one
    if (new.target === ScEnvMetaEntryBase) {
      throw new TypeError(
        "new xdr.ScEnvMetaEntry(...) is not supported: XDR unions are built from " +
          "per-variant factories. Call xdr.ScEnvMetaEntry.scEnvMetaKindInterfaceVersion(...) " +
          "(or another arm factory) instead.",
      );
    }
  }

  static readonly schema: XdrType<ScEnvMetaEntryWire> = union(
    "ScEnvMetaEntry",
    {
      switchOn: ScEnvMetaKind.schema,
      cases: [
        case_(
          "scEnvMetaKindInterfaceVersion",
          0,
          field("interfaceVersion", ScEnvMetaEntryInterfaceVersion.schema),
        ),
      ],
      switchKey: "kind",
    },
  );

  static scEnvMetaKindInterfaceVersion(
    interfaceVersion: ScEnvMetaEntryInterfaceVersion,
  ): ScEnvMetaEntryInterfaceVersionArm {
    return new ScEnvMetaEntryInterfaceVersionArm(interfaceVersion);
  }

  static fromXdrObject(wire: ScEnvMetaEntryWire): ScEnvMetaEntry {
    switch (wire.kind) {
      case 0:
        return new ScEnvMetaEntryInterfaceVersionArm(
          ScEnvMetaEntryInterfaceVersion.fromXdrObject(wire.interfaceVersion),
        );
    }
    // unreachable for a well-typed wire object; a hand-built one can still
    // carry an out-of-range discriminant
    throw new XdrError(
      `ScEnvMetaEntry: unknown kind ${(wire as { kind: unknown }).kind}`,
    );
  }

  /**
   * Type guard narrowing an unknown value to a concrete ScEnvMetaEntry variant.
   * Use this instead of `instanceof ScEnvMetaEntry`: the exported `ScEnvMetaEntry` value
   * is the abstract base, so `instanceof` narrows to the base (not the
   * variant union) and forces a cast. `ScEnvMetaEntry.is(x)` narrows to the union.
   */
  static is(value: unknown): value is ScEnvMetaEntry {
    return value instanceof ScEnvMetaEntryBase;
  }

  abstract toXdrObject(): ScEnvMetaEntryWire;
}

export class ScEnvMetaEntryInterfaceVersionArm extends ScEnvMetaEntryBase {
  readonly type = "scEnvMetaKindInterfaceVersion" as const;
  readonly interfaceVersion: ScEnvMetaEntryInterfaceVersion;

  constructor(interfaceVersion: ScEnvMetaEntryInterfaceVersion) {
    super();
    this.interfaceVersion = interfaceVersion;
  }

  get value(): ScEnvMetaEntryInterfaceVersion {
    return this.interfaceVersion;
  }

  toXdrObject(): Extract<ScEnvMetaEntryWire, { kind: 0 }> {
    return { kind: 0, interfaceVersion: this.interfaceVersion.toXdrObject() };
  }
}

export type ScEnvMetaEntry = ScEnvMetaEntryInterfaceVersionArm;
export const ScEnvMetaEntry = ScEnvMetaEntryBase;
